import type { ComponentType, SVGProps } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import { Container } from "../components/Container";
import { HashLink } from "../components/HashLink";
import { Logo } from "../components/Logo";
import {
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
} from "../components/SocialIcons";
import { useLocale } from "../hooks/useLocale";
import { FOOTER_LEGAL_SLUGS, getLegalDocument } from "../lib/legal/documents";
import { BRAND } from "../lib/constants";

type SocialIconComponent = ComponentType<SVGProps<SVGSVGElement>>;
type SocialLink = { label: string; href: string; Icon: SocialIconComponent };

const SOCIALS: ReadonlyArray<SocialLink> = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/albavox_/",
    Icon: InstagramIcon,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61592069065073",
    Icon: FacebookIcon,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/albaavox/",
    Icon: LinkedInIcon,
  },
];

const PRODUCT_LINKS = [
  { key: "features", href: "#features" },
  { key: "how_it_works", href: "#how-it-works" },
  { key: "esim", href: "/esim/shop" },
  { key: "download", href: "#download" },
  { key: "pricing", href: "#pricing" },
] as const;

const COMPANY_LINKS = [
  { key: "about", href: "#about" },
  { key: "blog", href: "/blog" },
  { key: "download", href: "#download" },
  { key: "careers", href: "#careers" },
  { key: "press", href: "#press" },
] as const;

export function Footer() {
  const { t } = useTranslation();
  const { pathFor, language, code } = useLocale();

  return (
    <footer
      className="border-t border-[color:var(--color-border-base)] bg-[#0A0D12] text-white"
      style={{ colorScheme: "dark" }}
    >
      <Container className="py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <Link
              to={pathFor("/")}
              className="inline-flex items-center"
              aria-label={`${BRAND.name} home`}
            >
              <Logo height={36} className="h-9 w-auto object-contain" />
            </Link>
            <p className="mt-5 max-w-xs text-sm text-white/70">
              {t("footer.tagline_long")}
            </p>
            <ul className="mt-6 flex items-center gap-3" dir="ltr">
              {SOCIALS.map(({ label, href, Icon }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/80 transition-colors duration-150 hover:border-white/40 hover:text-white"
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Product */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-white">
              {t("footer.product")}
            </h2>
            <ul className="mt-5 space-y-3">
              {PRODUCT_LINKS.map((l) => (
                <li key={l.key}>
                  {l.href.startsWith("#") ? (
                    <HashLink
                      to={l.href}
                      className="text-sm text-white/70 transition-colors duration-150 hover:text-white"
                    >
                      {t(`footer.product_links.${l.key}`)}
                    </HashLink>
                  ) : (
                    <Link
                      to={pathFor(l.href)}
                      className="text-sm text-white/70 transition-colors duration-150 hover:text-white"
                    >
                      {t(`footer.product_links.${l.key}`)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-white">
              {t("footer.company")}
            </h2>
            <ul className="mt-5 space-y-3">
              {COMPANY_LINKS.map((l) => (
                <li key={l.key}>
                  {l.href.startsWith("#") ? (
                    <a
                      href={l.href}
                      className="text-sm text-white/70 transition-colors duration-150 hover:text-white"
                    >
                      {t(`footer.company_links.${l.key}`)}
                    </a>
                  ) : (
                    <Link
                      to={pathFor(l.href)}
                      className="text-sm text-white/70 transition-colors duration-150 hover:text-white"
                    >
                      {t(`footer.company_links.${l.key}`)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-white">
              {t("footer.legal")}
            </h2>
            <ul className="mt-5 space-y-3">
              {FOOTER_LEGAL_SLUGS.map((slug) => {
                const doc = getLegalDocument(slug, code);
                return (
                  <li key={slug}>
                    <Link
                      to={pathFor(`/${slug}`)}
                      className="text-sm text-white/70 transition-colors duration-150 hover:text-white"
                    >
                      {t(`footer.legal_links.${slug}`, {
                        defaultValue: doc?.title ?? slug,
                      })}
                    </Link>
                  </li>
                );
              })}
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="text-sm text-white/70 transition-colors duration-150 hover:text-white"
                >
                  {t("footer.legal_links.contact")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
          <p className="text-xs text-white/60">{t("footer.rights")}</p>
          <div className="inline-flex items-center gap-2 text-xs text-white/60">
            <span aria-hidden="true">{language.flag}</span>
            <span>
              {language.nativeName}{" "}
              <span className="opacity-60">· {language.name}</span>
            </span>
          </div>
        </div>
      </Container>
    </footer>
  );
}
